// Shared data loader used by every pure-client report. Pulls the three
// window-scoped search roots once (sessions, tasks, legs) and shares
// them across report tabs so switching tabs doesn't re-fetch.
//
// Caller owns the from/to window via the shared Analytics date-range form.
// Each report derives its view from the same base dataset.

import { useCallback, useState } from 'react';
import { runSearchQuery } from '../../../api_scripts/wxccSearchClient';

const AI_SESSIONS_QUERY = `
  query AiSessions($from: Long!, $to: Long!) {
    contactAIAgentSession(from: $from, to: $to) {
      data {
        interactionId sessionId agentId agentName agentType agentLanguage
        agentAIEngine interactionOutcome intent channelType ani dnis
        entryPoint { id name }
        flow { id name }
        sessionStartTime sessionEndTime duration
        noOfTotalMessages averageResponseTime totalIntentCompleted
        dialogCount lastAction totalActionCompleted
        deadAirCount evalInteractionsFailureCount bargeInCount
        fulfillmentCount welcomeMessageResponseTime
        overallEvalScore wordRatioTotalScore
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

const TASK_DETAILS_QUERY = `
  query TaskDetails($from: Long!, $to: Long!) {
    taskDetails(from: $from, to: $to) {
      tasks {
        id status channelType direction createdTime endedTime totalDuration
        firstQueueId firstQueueName
        lastQueue { id name }
        lastAgent { id name }
        lastTeam { id name }
        lastSite { id name }
        entryPoint { id name }
        terminationType terminationReason isContactHandled
        lastWrapupCodeName
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

const TASK_LEG_DETAILS_QUERY = `
  query TaskLegDetails($from: Long!, $to: Long!) {
    taskLegDetails(from: $from, to: $to) {
      taskLegs {
        id taskId createdTime endedTime channelType direction handleType
        callLegType handleTime ringingDuration connectedDuration
        holdDuration wrapupDuration terminationReason abandonedType
        queue { id name }
        owner { id name }
        team { id name }
        site { id name }
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

// The OPS endpoint rate-limits at ~1 req / 2s. Serialise.
const RATE_GAP_MS = 2200;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function useReportWindow() {
  const [state, setState] = useState({ loading: false, data: null, error: null, fetchedAt: null, from: null, to: null });

  const fetchWindow = useCallback(async (from, to) => {
    setState({ loading: true, data: null, error: null, fetchedAt: null, from, to });
    try {
      const ai = await runSearchQuery(AI_SESSIONS_QUERY, { from, to });
      await sleep(RATE_GAP_MS);
      const tasks = await runSearchQuery(TASK_DETAILS_QUERY, { from, to });
      await sleep(RATE_GAP_MS);
      const legs = await runSearchQuery(TASK_LEG_DETAILS_QUERY, { from, to });

      setState({
        loading: false,
        data: {
          sessions: ai?.contactAIAgentSession?.data ?? [],
          sessionsTruncated: !!ai?.contactAIAgentSession?.pageInfo?.hasNextPage,
          tasks: tasks?.taskDetails?.tasks ?? [],
          tasksTruncated: !!tasks?.taskDetails?.pageInfo?.hasNextPage,
          legs: legs?.taskLegDetails?.taskLegs ?? [],
          legsTruncated: !!legs?.taskLegDetails?.pageInfo?.hasNextPage,
        },
        error: null,
        fetchedAt: Date.now(),
        from,
        to,
      });
    } catch (e) {
      setState({ loading: false, data: null, error: e.message, fetchedAt: null, from, to });
    }
  }, []);

  return { ...state, fetchWindow };
}
